import React, { useId, useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';
import { getEcolabelsByKeys } from '../../config/ecolabels';
import { useValuePriorities } from '../../hooks/useValuePriorities';

import EcolabelList from '../EcolabelList/EcolabelList';
import ValuePrioritiesPicker from './ValuePrioritiesPicker';

import css from './ValuePriorities.module.css';

/**
 * A compact "Your priorities" bar that opens the picker in place.
 *
 * Closed, it shows the shopper's priorities as small badges (or a hint to set
 * some), plus any page-specific actions — e.g. the browse page's "Show products
 * with my priorities". Open, the full picker sits underneath.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className]
 * @param {ReactNode} [props.actions] extra buttons, shown once priorities are set
 * @param {boolean} [props.showSummary=true] show the title, badges and hint; when
 *   false only the toggle is shown (the product page already says what matches)
 * @returns {JSX.Element}
 */
const ValuePrioritiesPanel = props => {
  const { className, actions = null, showSummary = true } = props;
  const priorities = useValuePriorities();
  const [isOpen, setIsOpen] = useState(false);
  const pickerId = useId();
  const hasPriorities = priorities.length > 0;

  const toggleMessageId = isOpen
    ? 'ValuePriorities.done'
    : hasPriorities
    ? 'ValuePriorities.edit'
    : 'ValuePriorities.set';

  return (
    <div className={classNames(showSummary ? css.panel : css.panelInline, className)}>
      <div className={css.panelHeader}>
        {showSummary ? (
          <div className={css.panelSummary}>
            <span className={css.panelTitle}>
              <FormattedMessage id="ValuePriorities.title" />
            </span>
            {hasPriorities ? (
              <EcolabelList
                ecolabels={getEcolabelsByKeys(priorities)}
                size="small"
                className={css.panelBadges}
              />
            ) : (
              <span className={css.panelHint}>
                <FormattedMessage id="ValuePriorities.hint" />
              </span>
            )}
          </div>
        ) : null}
        <div className={css.panelActions}>
          {hasPriorities ? actions : null}
          <button
            type="button"
            className={css.toggleButton}
            aria-expanded={isOpen}
            aria-controls={pickerId}
            onClick={() => setIsOpen(open => !open)}
          >
            <FormattedMessage id={toggleMessageId} />
          </button>
        </div>
      </div>
      {isOpen ? <ValuePrioritiesPicker id={pickerId} className={css.panelPicker} /> : null}
    </div>
  );
};

export default ValuePrioritiesPanel;
